'use client';

import { useEffect, useState } from 'react';
import { DatePicker } from '@core/ui/datepicker';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import {
  type FieldErrors,
  type FieldPath,
  type FieldPathValue,
  useForm,
} from 'react-hook-form';
import {
  PiBuildings,
  PiCalendarBlank,
  PiCaretDownBold,
  PiChatCircle,
  PiEnvelope,
  PiMapPin,
  PiPaperPlaneTilt,
  PiPhone,
  PiTruck,
  PiTag,
  PiUser,
} from 'react-icons/pi';
import { Button, Checkbox, Input, Select, Textarea } from 'rizzui';
import { getPublicContactLookups } from '@/server/api';
import { quoteFormSchema, type QuoteFormValues } from './get-quote-schema';
import { GetQuoteSuccess } from './get-quote-success';

type LookupOption = {
  value: number;
  label: string;
};

const INITIAL_FORM: QuoteFormValues = {
  service: '',
  type: null,
  company: '',
  mcNumber: '',
  name: '',
  email: '',
  phone: '',
  origin: '',
  destination: '',
  pickupDate: null,
  deliveryDate: null,
  freightDescription: '',
  weight: '',
  dimensions: '',
  pieces: '',
  equipment: '',
  regulatedMaterials: '',
  accessorials: '',
  subject: '',
  message: '',
  contactMethod: null,
  preferredTime: null,
  warehouseLocation: '',
  storageType: '',
  storageDuration: '',
  estimatedSpace: '',
  palletUnits: '',
  requestedStartDate: null,
  inboundFrequency: '',
  warehouseCustomerType: '',
  specialHandling: [],
  valueAddedServices: [],
};

const controlClass =
  'h-12 rounded-lg !border border-[#dce4ef] bg-white text-[15px] shadow-none focus:!border-[#119df1] focus:!outline-none focus:!ring-0 focus-visible:!outline-none focus-visible:!ring-0';

const serviceOptions = [
  { value: 'full-truckload', label: 'Full Truckload (FTL)' },
  { value: 'less-than-truckload', label: 'Less Than Truckload (LTL)' },
  { value: 'partial-truckload', label: 'Partial Truckload (PTL)' },
  { value: 'dry-van', label: 'Dry Van' },
  { value: 'reefer', label: 'Reefer' },
  { value: 'flatbed', label: 'Flatbed / Open Deck' },
  { value: 'heavy-haul', label: 'Heavy Haul' },
  { value: 'intermodal', label: 'Intermodal Transportation' },
  { value: 'drayage', label: 'Drayage' },
  { value: 'expedited', label: 'Expedited Freight' },
  { value: 'last-mile-delivery', label: 'Last Mile Delivery' },
  {
    value: 'sustainable-green-logistics',
    label: 'Sustainable / Green Logistics',
  },
  { value: 'warehousing', label: 'Warehousing' },
  { value: 'managed-transportation', label: 'Managed Transportation' },
  { value: 'other', label: 'Other / Not Sure' },
];

const equipmentOptions = [
  { value: 'dry-van', label: 'Dry Van' },
  { value: 'reefer', label: 'Reefer' },
  { value: 'flatbed', label: 'Flatbed / Open Deck' },
  { value: 'heavy-haul', label: 'Heavy Haul / Specialized' },
  { value: 'intermodal', label: 'Intermodal / Container' },
  { value: 'other', label: 'Other / Not Sure' },
];

const regulatedMaterialOptions = [
  { value: 'no', label: 'No' },
  { value: 'yes', label: 'Yes' },
  { value: 'unsure', label: 'Not Sure' },
];

const storageTypeOptions = [
  { value: 'pallet', label: 'Pallet' },
  { value: 'bulk', label: 'Bulk' },
  { value: 'rack', label: 'Rack' },
  { value: 'cross-dock', label: 'Cross-Dock' },
  { value: 'bonded-ftz', label: 'Bonded / FTZ' },
  { value: 'cold-storage', label: 'Cold Storage' },
  { value: 'other', label: 'Other' },
];

const storageDurationOptions = [
  { value: 'short-term', label: 'Short-term' },
  { value: 'month-to-month', label: 'Month-to-month' },
  { value: '3-6-months', label: '3–6 months' },
  { value: '6-12-months', label: '6–12 months' },
  { value: '12-plus-months', label: '12+ months' },
];

const inboundFrequencyOptions = [
  { value: 'one-time', label: 'One-time' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'ongoing', label: 'Ongoing replenishment' },
];

const warehouseCustomerTypeOptions = [
  { value: 'shipper-manufacturer', label: 'Shipper / Manufacturer' },
  { value: '3pl', label: '3PL' },
  { value: 'freight-broker', label: 'Freight Broker' },
  { value: 'retailer-distributor', label: 'Retailer / Distributor' },
  { value: 'other', label: 'Other' },
];

const specialHandlingOptions = [
  { value: 'temperature-controlled', label: 'Temperature-controlled' },
  { value: 'hazmat', label: 'Hazmat' },
  { value: 'high-value-secure', label: 'High-value / secure' },
  { value: 'stackable', label: 'Stackable' },
  { value: 'fragile', label: 'Fragile' },
  { value: 'none', label: 'None' },
];

const valueAddedServiceOptions = [
  { value: 'pick-pack', label: 'Pick & Pack' },
  { value: 'kitting', label: 'Kitting' },
  { value: 'labeling', label: 'Labeling' },
  { value: 'wms-inventory-access', label: 'WMS / Inventory Access' },
  { value: 'cross-docking', label: 'Cross-Docking' },
  {
    value: 'distribution-fulfillment',
    label: 'Distribution / Fulfillment',
  },
];

function selectedOptionLabel(
  options: ReadonlyArray<{ value: string; label: string }>,
  value: string
) {
  return options.find((option) => option.value === value)?.label ?? value;
}

function selectedOptionLabels(
  options: ReadonlyArray<{ value: string; label: string }>,
  values: string[]
) {
  return values.map((value) => selectedOptionLabel(options, value)).join(', ');
}

function normalizeService(value: string) {
  return serviceOptions.some((option) => option.value === value) ? value : '';
}

function serviceLabel(value: string) {
  return (
    serviceOptions.find((option) => option.value === value)?.label ?? value
  );
}

const fieldLabels: Partial<Record<keyof QuoteFormValues, string>> = {
  service: 'Service requested',
  type: 'What best describes you',
  company: 'Company',
  mcNumber: 'MC number',
  name: 'Your name',
  email: 'Email address',
  phone: 'Phone number',
  origin: 'Origin state',
  destination: 'Destination state',
  pickupDate: 'Requested pickup date',
  deliveryDate: 'Requested delivery date',
  freightDescription: 'Freight description',
  weight: 'Approximate weight',
  equipment: 'Equipment type',
  warehouseLocation: 'Warehouse location',
  storageType: 'Storage type',
  storageDuration: 'Storage duration',
  estimatedSpace: 'Estimated space',
  requestedStartDate: 'Requested start date',
  warehouseCustomerType: 'Customer type',
};

export function GetQuoteForm({
  initialEmail = '',
  initialService = '',
}: {
  initialEmail?: string;
  initialService?: string;
}) {
  const {
    formState: { errors, submitCount },
    handleSubmit: handleValidatedSubmit,
    reset,
    setValue,
    trigger,
    watch,
  } = useForm<QuoteFormValues>({
    resolver: zodResolver(quoteFormSchema),
    defaultValues: {
      ...INITIAL_FORM,
      email: initialEmail,
      service: normalizeService(initialService),
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldFocusError: false,
  });
  const form = watch();
  const [status, setStatus] = useState<
    'idle' | 'sending' | 'success' | 'error'
  >('idle');
  const [submitError, setSubmitError] = useState('');
  const [contactTypes, setContactTypes] = useState<LookupOption[]>([]);
  const [contactMethods, setContactMethods] = useState<LookupOption[]>([]);
  const [lookupsLoading, setLookupsLoading] = useState(true);
  const [submittedService, setSubmittedService] = useState('');
  const isWarehousing = form.service === 'warehousing';
  const shipperInquiryType = contactTypes.find((option) =>
    option.label.toLowerCase().includes('shipper')
  ) ?? { value: 1, label: 'Shipper / Customer' };

  useEffect(() => {
    if (!initialEmail) return;
    setValue('email', initialEmail);
  }, [initialEmail, setValue]);

  useEffect(() => {
    const service = normalizeService(initialService);
    if (!service) return;
    setValue('service', service);
  }, [initialService, setValue]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadLookups() {
      try {
        const lookups = await getPublicContactLookups(controller.signal);
        const inquiryTypes = lookups['contact-inquiry-type'].filter(
          (option): option is LookupOption => typeof option.value === 'number'
        );
        const preferredMethods = lookups['preferred-contact-method'].filter(
          (option): option is LookupOption => typeof option.value === 'number'
        );

        setContactTypes(inquiryTypes);
        setContactMethods(preferredMethods);
      } catch (lookupError) {
        if (controller.signal.aborted) return;
        setStatus('error');
        setSubmitError(
          lookupError instanceof Error
            ? lookupError.message
            : 'Contact options are unavailable.'
        );
      } finally {
        if (!controller.signal.aborted) setLookupsLoading(false);
      }
    }

    void loadLookups();
    return () => controller.abort();
  }, []);

  function update<Key extends FieldPath<QuoteFormValues>>(
    key: Key,
    value: FieldPathValue<QuoteFormValues, Key>
  ) {
    setValue(key, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: submitCount > 0,
    });
    if (key === 'service' && submitCount > 0) void trigger();
    if (status !== 'idle') setStatus('idle');
    if (submitError) setSubmitError('');
  }

  function toggleWarehouseOption(
    key: 'specialHandling' | 'valueAddedServices',
    value: string
  ) {
    const selected = form[key];
    const isSelected = selected.includes(value);
    let nextValues: string[];

    if (key === 'specialHandling' && value === 'none') {
      nextValues = isSelected ? [] : ['none'];
    } else {
      const withoutNone =
        key === 'specialHandling'
          ? selected.filter((item) => item !== 'none')
          : selected;
      nextValues = isSelected
        ? withoutNone.filter((item) => item !== value)
        : [...withoutNone, value];
    }

    update(key, nextValues);
  }

  function handleInvalid(validationErrors: FieldErrors<QuoteFormValues>) {
    const firstField = Object.keys(validationErrors)[0] as
      | keyof QuoteFormValues
      | undefined;
    if (!firstField) return;

    const fieldError = validationErrors[firstField];
    const message =
      typeof fieldError?.message === 'string'
        ? fieldError.message
        : 'Please review this field.';
    setStatus('error');
    setSubmitError(
      `Please check ${fieldLabels[firstField] ?? 'the highlighted field'}: ${message}`
    );

    requestAnimationFrame(() => {
      const target = document.getElementById(`quote-${firstField}`);
      const focusTarget = target?.matches(
        'button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
      )
        ? target
        : target?.querySelector<HTMLElement>(
            'button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
          );
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (focusTarget as HTMLElement | null)?.focus({ preventScroll: true });
    });
  }

  async function submitForm(form: QuoteFormValues) {
    setSubmitError('');
    setStatus('sending');
    try {
      const preferredContactMethod = form.contactMethod
        ? contactMethods.find((option) => option.value === form.contactMethod)
            ?.label
        : '';
      const preferredContactDetails = [
        preferredContactMethod
          ? `Preferred contact method: ${preferredContactMethod}`
          : '',
        form.preferredTime
          ? `Preferred contact time: ${dayjs(form.preferredTime).format('YYYY-MM-DD HH:mm')}`
          : '',
      ];

      const companyName = form.mcNumber.trim()
        ? `${form.company.trim()} (MC numbers: ${form.mcNumber.trim()})`
        : form.company.trim();

      const payload = isWarehousing
        ? {
            type: shipperInquiryType.value,
            warehouse_location_needed: form.warehouseLocation.trim(),
            storage_type: selectedOptionLabel(
              storageTypeOptions,
              form.storageType
            ),
            storage_duration: selectedOptionLabel(
              storageDurationOptions,
              form.storageDuration
            ),
            estimated_space_needed: form.estimatedSpace.trim(),
            number_of_pallets_or_units: form.palletUnits.trim(),
            requested_start_date: dayjs(form.requestedStartDate).format(
              'MM/DD/YYYY'
            ),
            inbound_frequency: form.inboundFrequency
              ? selectedOptionLabel(
                  inboundFrequencyOptions,
                  form.inboundFrequency
                )
              : '',
            customer_type: selectedOptionLabel(
              warehouseCustomerTypeOptions,
              form.warehouseCustomerType
            ),
            company_name: companyName,
            contact_name: form.name.trim(),
            email: form.email.trim(),
            phone: form.phone.trim(),
            subject: form.subject.trim() || 'Warehousing quote request',
            special_handling_requirements: form.specialHandling.map((value) =>
              selectedOptionLabel(specialHandlingOptions, value)
            ),
            value_added_services_needed: form.valueAddedServices.map((value) =>
              selectedOptionLabel(valueAddedServiceOptions, value)
            ),
            freight_description: form.freightDescription.trim(),
            additional_notes: [form.message.trim(), ...preferredContactDetails]
              .filter(Boolean)
              .join('\n'),
            message: [
              `Service requested: ${serviceLabel(form.service)}`,
              `Warehouse location needed: ${form.warehouseLocation.trim()}`,
              `Storage type: ${selectedOptionLabel(storageTypeOptions, form.storageType)}`,
              `Storage duration: ${selectedOptionLabel(storageDurationOptions, form.storageDuration)}`,
              `Estimated space needed: ${form.estimatedSpace.trim()}`,
              form.palletUnits.trim()
                ? `Number of pallets / units: ${form.palletUnits.trim()}`
                : '',
              `Requested start date: ${dayjs(form.requestedStartDate).format('MM/DD/YYYY')}`,
              form.inboundFrequency
                ? `Inbound frequency: ${selectedOptionLabel(inboundFrequencyOptions, form.inboundFrequency)}`
                : '',
              `Customer type: ${selectedOptionLabel(warehouseCustomerTypeOptions, form.warehouseCustomerType)}`,
              `Freight description: ${form.freightDescription.trim()}`,
              form.message.trim()
                ? `Additional notes: ${form.message.trim()}`
                : '',
              ...preferredContactDetails,
            ]
              .filter(Boolean)
              .join('\n'),
          }
        : {
            type: form.type as number,
            company_name: companyName,
            contact_name: form.name.trim(),
            email: form.email.trim(),
            phone: form.phone.trim(),
            origin_state: form.origin.trim(),
            destination_state: form.destination.trim(),
            equipment_type: selectedOptionLabel(
              equipmentOptions,
              form.equipment
            ),
            approximate_weight_or_volume: `${form.weight.trim()} lbs`,
            target_pickup_date: dayjs(form.pickupDate).format('YYYY-MM-DD'),
            message: [
              `Service requested: ${serviceLabel(form.service)}`,
              form.type
                ? `Customer type: ${contactTypes.find((option) => option.value === form.type)?.label ?? form.type}`
                : '',
              form.subject.trim() ? `Subject: ${form.subject.trim()}` : '',
              `Requested delivery date: ${dayjs(form.deliveryDate).format('YYYY-MM-DD')}`,
              `Freight description: ${form.freightDescription.trim()}`,
              form.dimensions.trim()
                ? `Dimensions: ${form.dimensions.trim()}`
                : '',
              form.pieces.trim()
                ? `Pieces / pallets: ${form.pieces.trim()}`
                : '',
              form.regulatedMaterials
                ? `Regulated materials: ${selectedOptionLabel(regulatedMaterialOptions, form.regulatedMaterials)}`
                : '',
              form.accessorials.trim()
                ? `Accessorial requirements: ${form.accessorials.trim()}`
                : '',
              form.message.trim()
                ? `Additional notes: ${form.message.trim()}`
                : '',
              ...preferredContactDetails,
            ]
              .filter(Boolean)
              .join('\n'),
          };

      const response = await fetch('/api/contact/shipper-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = (await response.json()) as {
        success?: boolean;
        message?: string;
      };
      if (!response.ok || result.success === false) {
        throw new Error(result.message || 'Request failed');
      }
      setSubmittedService(serviceLabel(form.service));
      reset({
        ...INITIAL_FORM,
        email: initialEmail,
        service: normalizeService(initialService),
      });
      setStatus('success');
    } catch (submitError) {
      setStatus('error');
      setSubmitError(
        submitError instanceof Error && submitError.message !== 'Request failed'
          ? submitError.message
          : 'We could not send your request. Please call (331)-249-2786.'
      );
    }
  }

  function fieldError(field: keyof QuoteFormValues) {
    const message = errors[field]?.message;
    return typeof message === 'string' ? message : undefined;
  }

  if (status === 'success') {
    return (
      <GetQuoteSuccess
        requestedService={submittedService}
        onReset={() => setStatus('idle')}
      />
    );
  }

  return (
    <form
      className="quote-form"
      onSubmit={handleValidatedSubmit(submitForm, handleInvalid)}
      noValidate
    >
      <div className="quote-form__heading">
        <span className="quote-form__heading-icon" aria-hidden="true">
          {isWarehousing ? <PiBuildings /> : <PiEnvelope />}
        </span>
        <div>
          <h2>
            {isWarehousing
              ? 'Request Warehouse Storage'
              : 'Request a Freight Quote'}
          </h2>
          <p>
            {isWarehousing
              ? "Tell us about your storage needs and we'll follow up with availability and pricing."
              : 'Share the lane, freight, timing, and service details our team needs to review the request.'}
          </p>
        </div>
      </div>

      <div className="quote-form__grid">
        <Select
          id="quote-service"
          size="lg"
          label="Service requested *"
          error={fieldError('service')}
          options={serviceOptions}
          placeholder="Select a freight service"
          value={form.service}
          onChange={(value: string) => update('service', value)}
          getOptionValue={(option) => option.value}
          displayValue={(value) =>
            serviceOptions.find((option) => option.value === value)?.label ??
            'Select a freight service'
          }
          prefix={<PiTruck className="quote-control-icon" />}
          suffix={<PiCaretDownBold className="h-3.5 w-3.5" />}
          selectClassName={controlClass}
          dropdownClassName="quote-select-dropdown"
          optionClassName="quote-select-option"
          inPortal={false}
          className="quote-form__full"
        />
        {!isWarehousing ? (
          <Select
            id="quote-type"
            size="lg"
            label="What best describes you? *"
            error={fieldError('type')}
            options={contactTypes}
            placeholder={
              lookupsLoading ? 'Loading options...' : 'Select an option'
            }
            value={form.type}
            onChange={(value: number) => update('type', value)}
            getOptionValue={(option) => option.value}
            displayValue={(value) =>
              contactTypes.find((option) => option.value === value)?.label ??
              'Select an option'
            }
            disabled={lookupsLoading}
            prefix={<PiUser className="quote-control-icon" />}
            suffix={<PiCaretDownBold className="h-3.5 w-3.5" />}
            selectClassName={controlClass}
            dropdownClassName="quote-select-dropdown"
            optionClassName="quote-select-option"
            inPortal={false}
          />
        ) : null}
        <Input
          id="quote-company"
          label="Company *"
          error={fieldError('company')}
          value={form.company}
          onChange={(event) => update('company', event.target.value)}
          placeholder="Enter company name"
          prefix={<PiBuildings className="quote-control-icon" />}
          inputClassName={controlClass}
          required
        />
        <Input
          id="quote-mcNumber"
          label="MC Number (Optional)"
          error={fieldError('mcNumber')}
          value={form.mcNumber}
          onChange={(event) => update('mcNumber', event.target.value)}
          placeholder="Enter MC number"
          inputMode="numeric"
          prefix={<PiTag className="quote-control-icon" />}
          inputClassName={controlClass}
        />
        <Input
          id="quote-name"
          label="Your Name *"
          error={fieldError('name')}
          value={form.name}
          onChange={(event) => update('name', event.target.value)}
          placeholder="Enter your full name"
          prefix={<PiUser className="quote-control-icon" />}
          inputClassName={controlClass}
          required
        />
        <Input
          id="quote-email"
          type="email"
          label="Email Address *"
          error={fieldError('email')}
          value={form.email}
          onChange={(event) => update('email', event.target.value)}
          placeholder="Enter your email address"
          prefix={<PiEnvelope className="quote-control-icon" />}
          inputClassName={controlClass}
          required
        />
        <Input
          id="quote-phone"
          type="tel"
          label="Phone Number *"
          error={fieldError('phone')}
          value={form.phone}
          onChange={(event) => update('phone', event.target.value)}
          placeholder="Enter your phone number"
          prefix={<PiPhone className="quote-control-icon" />}
          inputClassName={controlClass}
          required
        />
        <Input
          label="Subject (Optional)"
          value={form.subject}
          onChange={(event) => update('subject', event.target.value)}
          placeholder="What is this regarding?"
          prefix={<PiTag className="quote-control-icon" />}
          inputClassName={controlClass}
        />
        {isWarehousing ? (
          <>
            <div className="quote-form__section-heading quote-form__full">
              <h3>Storage Requirements</h3>
              <p>
                Share the location, space, timing, and storage setup you need.
              </p>
            </div>
            <Input
              id="quote-warehouseLocation"
              label="Warehouse Location Needed *"
              error={fieldError('warehouseLocation')}
              value={form.warehouseLocation}
              onChange={(event) =>
                update('warehouseLocation', event.target.value)
              }
              placeholder="City, state, ZIP/postal code, or address"
              prefix={<PiMapPin className="quote-control-icon" />}
              inputClassName={controlClass}
              className="quote-form__full"
              required
            />
            <Select
              id="quote-storageType"
              size="lg"
              label="Storage Type *"
              error={fieldError('storageType')}
              options={storageTypeOptions}
              placeholder="Select a storage type"
              value={form.storageType}
              onChange={(value: string) => update('storageType', value)}
              getOptionValue={(option) => option.value}
              displayValue={(value) =>
                storageTypeOptions.find((option) => option.value === value)
                  ?.label ?? 'Select a storage type'
              }
              prefix={<PiBuildings className="quote-control-icon" />}
              suffix={<PiCaretDownBold className="h-3.5 w-3.5" />}
              selectClassName={controlClass}
              dropdownClassName="quote-select-dropdown"
              optionClassName="quote-select-option"
              inPortal={false}
            />
            <Select
              id="quote-storageDuration"
              size="lg"
              label="Storage Duration *"
              error={fieldError('storageDuration')}
              options={storageDurationOptions}
              placeholder="Select a storage duration"
              value={form.storageDuration}
              onChange={(value: string) => update('storageDuration', value)}
              getOptionValue={(option) => option.value}
              displayValue={(value) =>
                storageDurationOptions.find((option) => option.value === value)
                  ?.label ?? 'Select a storage duration'
              }
              prefix={<PiCalendarBlank className="quote-control-icon" />}
              suffix={<PiCaretDownBold className="h-3.5 w-3.5" />}
              selectClassName={controlClass}
              dropdownClassName="quote-select-dropdown"
              optionClassName="quote-select-option"
              inPortal={false}
            />
            <Input
              id="quote-estimatedSpace"
              label="Estimated Space Needed *"
              error={fieldError('estimatedSpace')}
              value={form.estimatedSpace}
              onChange={(event) => update('estimatedSpace', event.target.value)}
              placeholder="e.g. 40 pallet positions, 5,000 sq ft, or not sure"
              prefix={<PiBuildings className="quote-control-icon" />}
              inputClassName={controlClass}
              required
            />
            <Input
              label="Number of Pallets / Units (Optional)"
              value={form.palletUnits}
              onChange={(event) => update('palletUnits', event.target.value)}
              placeholder="Approximate count"
              prefix={<PiTag className="quote-control-icon" />}
              inputClassName={controlClass}
            />
            <DatePicker
              selected={form.requestedStartDate}
              onChange={(date: Date | null) =>
                update('requestedStartDate', date)
              }
              dateFormat="MMM d, yyyy"
              placeholderText="Select requested start date"
              popperClassName="quote-date-picker-popper z-[10020]"
              calendarClassName="quote-date-picker-calendar"
              inputProps={{
                id: 'quote-requestedStartDate',
                label: 'Requested Start Date *',
                error: fieldError('requestedStartDate'),
                prefix: <PiCalendarBlank className="quote-control-icon" />,
                inputClassName: controlClass,
              }}
            />
            <Select
              size="lg"
              label="Inbound Frequency (Optional)"
              options={inboundFrequencyOptions}
              placeholder="Select inbound frequency"
              value={form.inboundFrequency}
              onChange={(value: string) => update('inboundFrequency', value)}
              getOptionValue={(option) => option.value}
              displayValue={(value) =>
                inboundFrequencyOptions.find((option) => option.value === value)
                  ?.label ?? 'Select inbound frequency'
              }
              prefix={<PiTruck className="quote-control-icon" />}
              suffix={<PiCaretDownBold className="h-3.5 w-3.5" />}
              selectClassName={controlClass}
              dropdownClassName="quote-select-dropdown"
              optionClassName="quote-select-option"
              inPortal={false}
            />
            <Select
              id="quote-warehouseCustomerType"
              size="lg"
              label="What Best Describes You? *"
              error={fieldError('warehouseCustomerType')}
              options={warehouseCustomerTypeOptions}
              placeholder="Select an option"
              value={form.warehouseCustomerType}
              onChange={(value: string) =>
                update('warehouseCustomerType', value)
              }
              getOptionValue={(option) => option.value}
              displayValue={(value) =>
                warehouseCustomerTypeOptions.find(
                  (option) => option.value === value
                )?.label ?? 'Select an option'
              }
              prefix={<PiUser className="quote-control-icon" />}
              suffix={<PiCaretDownBold className="h-3.5 w-3.5" />}
              selectClassName={controlClass}
              dropdownClassName="quote-select-dropdown"
              optionClassName="quote-select-option"
              inPortal={false}
              className="quote-form__full"
            />
            <fieldset className="quote-checkbox-group quote-form__full">
              <legend>Special Handling Requirements (Optional)</legend>
              <div className="quote-checkbox-grid">
                {specialHandlingOptions.map((option) => (
                  <Checkbox
                    key={option.value}
                    label={option.label}
                    variant="flat"
                    checked={form.specialHandling.includes(option.value)}
                    onChange={() =>
                      toggleWarehouseOption('specialHandling', option.value)
                    }
                    className="quote-checkbox"
                    inputClassName="quote-checkbox__input"
                  />
                ))}
              </div>
            </fieldset>
            <fieldset className="quote-checkbox-group quote-form__full">
              <legend>Value-Added Services Needed (Optional)</legend>
              <div className="quote-checkbox-grid">
                {valueAddedServiceOptions.map((option) => (
                  <Checkbox
                    key={option.value}
                    label={option.label}
                    variant="flat"
                    checked={form.valueAddedServices.includes(option.value)}
                    onChange={() =>
                      toggleWarehouseOption('valueAddedServices', option.value)
                    }
                    className="quote-checkbox"
                    inputClassName="quote-checkbox__input"
                  />
                ))}
              </div>
            </fieldset>
            <div className="quote-form__full quote-textarea-wrap">
              <Textarea
                id="quote-freightDescription"
                label="Freight Description *"
                error={fieldError('freightDescription')}
                value={form.freightDescription}
                onChange={(event) =>
                  update('freightDescription', event.target.value)
                }
                placeholder="Commodity, packaging, storage conditions, handling needs, and relevant product details..."
                textareaClassName="min-h-[112px] rounded-lg border-[#dce4ef] bg-white !pl-10 text-[15px] shadow-none focus:!border-[#119df1] focus:!outline-none focus:!ring-0 focus-visible:!outline-none focus-visible:!ring-0"
                required
              />
              <PiTag className="quote-textarea-wrap__icon" aria-hidden="true" />
            </div>
          </>
        ) : (
          <>
            <Input
              id="quote-origin"
              label="Origin State *"
              error={fieldError('origin')}
              value={form.origin}
              onChange={(event) => update('origin', event.target.value)}
              placeholder="e.g. CA"
              prefix={<PiMapPin className="quote-control-icon" />}
              inputClassName={controlClass}
              required
            />
            <Input
              id="quote-destination"
              label="Destination State *"
              error={fieldError('destination')}
              value={form.destination}
              onChange={(event) => update('destination', event.target.value)}
              placeholder="e.g. TX"
              prefix={<PiMapPin className="quote-control-icon" />}
              inputClassName={controlClass}
              required
            />
            <DatePicker
              selected={form.pickupDate}
              onChange={(date: Date | null) => update('pickupDate', date)}
              dateFormat="MMM d, yyyy"
              placeholderText="Select requested pickup date"
              popperClassName="quote-date-picker-popper z-[10020]"
              calendarClassName="quote-date-picker-calendar"
              inputProps={{
                id: 'quote-pickupDate',
                label: 'Requested Pickup Date *',
                error: fieldError('pickupDate'),
                prefix: <PiCalendarBlank className="quote-control-icon" />,
                inputClassName: controlClass,
              }}
            />
            <DatePicker
              selected={form.deliveryDate}
              onChange={(date: Date | null) => update('deliveryDate', date)}
              dateFormat="MMM d, yyyy"
              placeholderText="Select requested delivery date"
              popperClassName="quote-date-picker-popper z-[10020]"
              calendarClassName="quote-date-picker-calendar"
              inputProps={{
                id: 'quote-deliveryDate',
                label: 'Requested Delivery Date *',
                error: fieldError('deliveryDate'),
                prefix: <PiCalendarBlank className="quote-control-icon" />,
                inputClassName: controlClass,
              }}
            />
            <div className="quote-form__full quote-textarea-wrap">
              <Textarea
                id="quote-freightDescription"
                label="Freight Description *"
                error={fieldError('freightDescription')}
                value={form.freightDescription}
                onChange={(event) =>
                  update('freightDescription', event.target.value)
                }
                placeholder="Commodity, packaging, handling needs, and relevant shipment details..."
                textareaClassName="min-h-[112px] rounded-lg border-[#dce4ef] bg-white !pl-10 text-[15px] shadow-none focus:!border-[#119df1] focus:!outline-none focus:!ring-0 focus-visible:!outline-none focus-visible:!ring-0"
                required
              />
              <PiTag className="quote-textarea-wrap__icon" aria-hidden="true" />
            </div>
            <Input
              id="quote-weight"
              label="Approximate Weight (lbs) *"
              error={fieldError('weight')}
              type="number"
              inputMode="decimal"
              min={0}
              step="any"
              value={form.weight}
              onChange={(event) => update('weight', event.target.value)}
              placeholder="e.g. 18000"
              prefix={<PiTag className="quote-control-icon" />}
              inputClassName={controlClass}
            />
            <Input
              label="Dimensions"
              value={form.dimensions}
              onChange={(event) => update('dimensions', event.target.value)}
              placeholder="Length × width × height per unit"
              prefix={<PiTag className="quote-control-icon" />}
              inputClassName={controlClass}
            />
            <Input
              label="Pieces / Pallets"
              value={form.pieces}
              onChange={(event) => update('pieces', event.target.value)}
              placeholder="Enter handling-unit count"
              prefix={<PiTag className="quote-control-icon" />}
              inputClassName={controlClass}
            />
            <Select
              size="lg"
              label="Regulated Materials"
              options={regulatedMaterialOptions}
              placeholder="Select an option"
              value={form.regulatedMaterials}
              onChange={(value: string) => update('regulatedMaterials', value)}
              getOptionValue={(option) => option.value}
              displayValue={(value) =>
                regulatedMaterialOptions.find(
                  (option) => option.value === value
                )?.label ?? 'Select an option'
              }
              prefix={<PiTag className="quote-control-icon" />}
              suffix={<PiCaretDownBold className="h-3.5 w-3.5" />}
              selectClassName={controlClass}
              dropdownClassName="quote-select-dropdown"
              optionClassName="quote-select-option"
              inPortal={false}
            />
            <Select
              id="quote-equipment"
              size="lg"
              label="Equipment Type *"
              error={fieldError('equipment')}
              options={equipmentOptions}
              placeholder="Select equipment type"
              value={form.equipment}
              onChange={(value: string) => update('equipment', value)}
              getOptionValue={(option) => option.value}
              displayValue={(value) =>
                equipmentOptions.find((option) => option.value === value)
                  ?.label ?? 'Select equipment type'
              }
              prefix={<PiTruck className="quote-control-icon" />}
              suffix={<PiCaretDownBold className="h-3.5 w-3.5" />}
              selectClassName={controlClass}
              dropdownClassName="quote-select-dropdown"
              optionClassName="quote-select-option"
              inPortal={false}
            />
            <Input
              label="Accessorial Requirements"
              value={form.accessorials}
              onChange={(event) => update('accessorials', event.target.value)}
              placeholder="Liftgate, appointment, inside delivery, limited access..."
              prefix={<PiTag className="quote-control-icon" />}
              inputClassName={controlClass}
            />
          </>
        )}
        <div className="quote-form__full quote-textarea-wrap">
          <Textarea
            label="Additional Notes (Optional)"
            value={form.message}
            onChange={(event) => update('message', event.target.value)}
            placeholder="Appointments, facility instructions, special handling, or other context..."
            textareaClassName="min-h-[132px] rounded-lg border-[#dce4ef] bg-white !pl-10 text-[15px] shadow-none focus:!border-[#119df1] focus:!outline-none focus:!ring-0 focus-visible:!outline-none focus-visible:!ring-0"
          />
          <PiChatCircle
            className="quote-textarea-wrap__icon"
            aria-hidden="true"
          />
        </div>
        <Select
          size="lg"
          label="Preferred Contact Method (Optional)"
          options={contactMethods}
          placeholder={
            lookupsLoading
              ? 'Loading options...'
              : 'Select your preferred method'
          }
          value={form.contactMethod}
          onChange={(value: number) => update('contactMethod', value)}
          getOptionValue={(option) => option.value}
          displayValue={(value) =>
            contactMethods.find((option) => option.value === value)?.label ??
            'Select your preferred method'
          }
          disabled={lookupsLoading}
          prefix={<PiEnvelope className="quote-control-icon" />}
          suffix={<PiCaretDownBold className="h-3.5 w-3.5" />}
          selectClassName={controlClass}
          dropdownClassName="quote-select-dropdown"
          optionClassName="quote-select-option"
          inPortal={false}
          className="quote-form__full"
        />
        <DatePicker
          selected={form.preferredTime}
          onChange={(date: Date | null) => update('preferredTime', date)}
          showTimeSelect
          timeIntervals={30}
          dateFormat="MMM d, yyyy h:mm aa"
          placeholderText="Select a preferred date and time"
          popperClassName="quote-date-picker-popper z-[10020]"
          calendarClassName="quote-date-picker-calendar"
          className="quote-form__full"
          inputProps={{
            label: 'Preferred Date & Time to Contact (Optional)',
            prefix: <PiCalendarBlank className="quote-control-icon" />,
            inputClassName: controlClass,
          }}
        />
      </div>

      {status === 'error' ? (
        <p
          className="quote-form__notice quote-form__notice--error"
          role="alert"
        >
          {submitError}
        </p>
      ) : null}

      <Button
        type="submit"
        className="quote-form__submit"
        isLoading={status === 'sending'}
        disabled={status === 'sending'}
      >
        <PiPaperPlaneTilt aria-hidden="true" />
        {isWarehousing ? 'Send Warehouse Request' : 'Send Message'}
      </Button>
    </form>
  );
}
